"use client";

import useSWR, { mutate as globalMutate } from "swr";
import { useAuth } from "@clerk/nextjs";
import { createFetcher } from "@/lib/api";
import type { VehiclesResponse, Vehicle } from "@/lib/api-types";
import { useCallback } from "react";

export function useVehicles(page = 1, limit = 25) {
  const { getToken } = useAuth();
  const { data, error, isLoading } = useSWR<VehiclesResponse>(
    `/api/v1/vehicles?page=${page}&limit=${limit}`,
    createFetcher(getToken),
  );
  return {
    vehicles: data?.vehicles ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? page,
    isLoading,
    error,
  };
}

export function useVehicle(id: string | undefined) {
  const { getToken } = useAuth();
  const { data, error, isLoading } = useSWR<Vehicle>(
    id ? `/api/v1/vehicles/${id}` : null,
    createFetcher(getToken),
  );
  return { vehicle: data, isLoading, error };
}

export function useVehicleMutations() {
  const { getToken } = useAuth();

  const create = useCallback(
    async (body: Partial<Vehicle>) => {
      const token = await getToken();
      const res = await fetch("/api/v1/vehicles", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await globalMutate((key: string) =>
        typeof key === "string" && key.startsWith("/api/v1/vehicles"),
      );
      return res.json();
    },
    [getToken],
  );

  const update = useCallback(
    async (id: string, body: Partial<Vehicle>) => {
      const token = await getToken();
      const res = await fetch(`/api/v1/vehicles/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await globalMutate((key: string) =>
        typeof key === "string" && key.startsWith("/api/v1/vehicles"),
      );
      return res.json();
    },
    [getToken],
  );

  const remove = useCallback(
    async (id: string) => {
      const token = await getToken();
      const res = await fetch(`/api/v1/vehicles/${id}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to delete vehicle");
      await globalMutate((key: string) =>
        typeof key === "string" && key.startsWith("/api/v1/vehicles"),
      );
    },
    [getToken],
  );

  return { create, update, remove };
}
